import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { CheckCircle, KeyRound } from 'lucide-react';
import { Container } from '../../components/styled/Container';
import { PageLayout } from '../../components/styled/PageLayout';
import { regionFromPathname } from '../../utils/regionFromPath';
import { pathWithRegion } from '../../utils/navBase';
import { resetPassword } from '../../services/authService';
import { useI18n } from '../../hooks/useI18n';

const Wrap = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
`;

const Card = styled(motion.div)`
  width: 100%;
  max-width: 26rem;
  padding: 2.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
`;

const Input = styled.input`
  width: 100%;
  height: 3rem;
  padding: 0 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  border: 2px solid transparent;
  margin-top: 0.6rem;
`;

const Btn = styled.button`
  margin-top: 1rem;
  width: 100%;
  height: 3rem;
  border: none;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 700;
  cursor: pointer;
`;

export default function ResetPasswordPage() {
  const location = useLocation();
  const region = regionFromPathname(location.pathname);
  const navigate = useNavigate();
  const search = new URLSearchParams(location.search);
  const [token, setToken] = useState(search.get('token') || '');
  const [newPassword, setNewPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const { t } = useI18n();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await resetPassword({ token, newPassword });
      setDone(true);
      setTimeout(() => navigate(pathWithRegion('/login', region)), 1200);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || t('auth.reset.failed', 'Reset failed'));
    }
  };

  return (
    <PageLayout>
      <Wrap>
        <Container style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <Card initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <h1 style={{ margin: 0 }}>{t('auth.reset.title', 'Reset password')}</h1>
            {done ? (
              <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                <CheckCircle size={40} style={{ color: 'hsl(142, 71%, 45%)' }} />
                <p>{t('auth.reset.done', 'Password updated. Redirecting to login...')}</p>
              </div>
            ) : (
              <form onSubmit={onSubmit}>
                <label>
                  {t('auth.reset.token', 'Reset token')}
                  <Input value={token} onChange={(e) => setToken(e.target.value)} required />
                </label>
                <label>
                  {t('auth.reset.newPassword', 'New password')}
                  <Input
                    type="password"
                    minLength={6}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </label>
                <Btn type="submit">
                  <KeyRound size={16} style={{ marginRight: 6, verticalAlign: '-2px' }} />
                  {t('auth.reset.updatePassword', 'Update password')}
                </Btn>
                {error ? <p style={{ color: '#ffb4b4' }}>{error}</p> : null}
                <p style={{ marginTop: '0.6rem' }}>
                  <Link to={pathWithRegion('/login', region)}>{t('auth.reset.backLogin', 'Back to login')}</Link>
                </p>
              </form>
            )}
          </Card>
        </Container>
      </Wrap>
    </PageLayout>
  );
}
